/**
 * Global search / command palette.
 *
 * Trigger:
 *   - Cmd/Ctrl + K from anywhere
 *   - Click on the trigger button rendered by <GlobalSearchTrigger />
 *
 * Searches across:
 *   - Projects (by id and name) → /portfolio/{id}
 *
 * Designed for the "exec who knows part of an id" case: someone types
 * "ETE-48" or "Catalog API" and lands on the right detail page in one
 * click. Keyboard-friendly: arrow keys + enter, esc to close.
 *
 * Data source reuses the existing React Query cache (usePortfolio), so
 * opening search on a page that's already loaded it is instant. Cold
 * open fetches the list on first mount.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FolderKanban, Search, X } from "lucide-react";
import { usePortfolio } from "@/hooks/usePortfolio";
import { cn } from "@/lib/cn";

type ResultKind = "project";

interface SearchResult {
  kind: ResultKind;
  id: string;
  name: string;
  subtitle: string | null;
  score: number;
  href: string;
}

const MAX_RESULTS = 12;

function scoreMatch(query: string, id: string, name: string): number {
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  const lid = id.toLowerCase();
  const lname = name.toLowerCase();
  // Exact id match wins.
  if (lid === q) return 1000;
  // Id prefix → very strong.
  if (lid.startsWith(q)) return 800;
  // Id contains → strong.
  if (lid.includes(q)) return 600;
  // Name startswith → moderate.
  if (lname.startsWith(q)) return 400;
  // Name contains → weak.
  if (lname.includes(q)) return 200;
  // Each word of the name startswith → weakest match.
  if (lname.split(/\s+/).some((w) => w.startsWith(q))) return 100;
  return 0;
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
}

function GlobalSearchModal({ open, onClose }: ModalProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Lazy-fetch source data only when the modal first opens. usePortfolio
  // is already mounted on most pages, so the data is typically a cache hit.
  const portfolio = usePortfolio(false);

  // Auto-focus on open + reset state.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Defer focus until after the modal renders.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim();
    if (!q) return [];
    const out: SearchResult[] = [];
    for (const p of portfolio.data ?? []) {
      const s = scoreMatch(q, p.id, p.name);
      if (s > 0) out.push({
        kind: "project",
        id: p.id,
        name: p.name,
        subtitle: p.status ? p.status.replace(/_/g, " ") : null,
        score: s,
        href: `/portfolio/${p.id}`,
      });
    }
    out.sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
    return out.slice(0, MAX_RESULTS);
  }, [query, portfolio.data]);

  // Reset activeIndex when results change so it never points past the end.
  useEffect(() => {
    if (activeIndex >= results.length) setActiveIndex(0);
  }, [results.length, activeIndex]);

  // Keyboard nav within modal.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const r = results[activeIndex];
        if (r) {
          navigate(r.href);
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, results, activeIndex, navigate, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/40 px-4 pt-[15vh]"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-xl bg-surface shadow-2xl ring-1 ring-line"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search className="h-4 w-4 shrink-0 text-ink-subtle" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search projects — try ETE-48 or 26-21"
            className="flex-1 border-0 bg-transparent py-3 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-0"
          />
          <button
            onClick={onClose}
            className="rounded-md p-1 text-ink-subtle hover:bg-raised hover:text-ink-3"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[50vh] overflow-y-auto">
          {query.trim().length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-ink-subtle">
              Start typing to search projects.
              <div className="mt-2 text-[10px] text-ink-faint">
                ↑↓ navigate · ↵ open · esc close
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-ink-muted">
              No matches for <span className="font-mono text-ink-2">{query}</span>.
            </div>
          ) : (
            <ul className="py-1">
              {results.map((r, i) => (
                <li key={`${r.kind}-${r.id}`}>
                  <button
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => {
                      navigate(r.href);
                      onClose();
                    }}
                    className={cn(
                      "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
                      i === activeIndex ? "bg-brand-navy-50 dark:bg-brand-navy-500/10" : "hover:bg-raised",
                    )}
                  >
                    <FolderKanban className="h-4 w-4 shrink-0 text-ink-subtle" />
                    <span className="shrink-0 rounded-md bg-raised px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-ink-2">
                      {r.id}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                      {r.name}
                    </span>
                    {r.subtitle && (
                      <span className="shrink-0 truncate text-[10px] capitalize text-ink-muted">
                        {r.subtitle}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t border-line-soft bg-canvas px-4 py-1.5 text-[10px] text-ink-subtle">
          <span>
            {(portfolio.data?.length ?? 0)} projects
          </span>
          <span className="font-mono">⌘K</span>
        </div>
      </div>
    </div>
  );
}

// Module-level open hook — set by GlobalSearchHost on mount, called by
// any GlobalSearchTrigger button or by the ⌘K listener. Single source of
// truth for the modal's open state means the trigger can render anywhere
// (top bar, page-specific spots, future palette additions) without each
// instance carrying its own modal.
let openSearch: (() => void) | null = null;

/**
 * Mount once globally (in AppShell). Owns the modal state and the
 * Cmd/Ctrl+K keyboard listener. Triggers anywhere call `openSearch()`
 * via the wrapper to pop it open.
 */
export function GlobalSearchHost() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    openSearch = () => setOpen(true);
    return () => { openSearch = null; };
  }, []);

  // Global Cmd/Ctrl+K opener.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
      if (!isCmdK) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return <GlobalSearchModal open={open} onClose={() => setOpen(false)} />;
}

/**
 * The visible search input shown in the TopBar on every page. Clicking
 * pops the centered modal; ⌘K still works regardless. Wider than a
 * sidebar chip so it reads as a proper search field — discoverability
 * is the point of putting it here.
 */
export function GlobalSearchTrigger() {
  return (
    <button
      onClick={() => openSearch?.()}
      className="flex w-72 items-center gap-2 rounded-md border border-line bg-canvas px-3 py-1.5 text-left text-xs text-ink-muted hover:bg-surface hover:border-line-strong hover:text-ink-2 transition-colors"
      title="Search projects (⌘K)"
    >
      <Search className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
      <span className="flex-1 truncate">Search projects…</span>
      <span className="shrink-0 rounded border border-line-strong bg-surface px-1 py-0 text-[9px] font-mono text-ink-muted">⌘K</span>
    </button>
  );
}
